import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { goodsIntakeApi } from '../../api/goods-intake-api';
import { ConflictError } from '../../api/errors';
import {
  acceptedQuantity,
  discrepancyKindLabels,
  lineDiscrepancies,
  type ExpectedReceipt,
  type PostReceiptResult,
} from '../../domain/goods-intake';
import type { StockReferences } from '../stock/use-stock-references';
import { intakeErrorMessage, isUnknownOutcome } from './error-message';

function Dialog({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="drawer-overlay centered-overlay" role="presentation" onClick={onClose}>
      <div
        className="confirmation-dialog intake-dialog form-surface"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
      >
        <div className="drawer-heading">
          <h2>{title}</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label={`Close ${title} dialog`}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Shows what posting will do (stock per location, and every shortage,
 * overage and damage) before it happens. Posting is safe to retry: the
 * server returns an already posted receipt unchanged. After a network
 * failure the outcome is shown as unknown, never as received.
 */
export function PostReceiptDialog({
  receipt,
  references,
  onClose,
  onPosted,
  onReload,
}: {
  receipt: ExpectedReceipt;
  references: StockReferences;
  onClose: () => void;
  onPosted: (result: PostReceiptResult) => void;
  onReload: () => void;
}) {
  const [error, setError] = useState<{ text: string; conflict: boolean } | null>(null);
  const [unknown, setUnknown] = useState(false);

  const post = useMutation({
    mutationFn: () => goodsIntakeApi.postReceipt(receipt.id, receipt.version),
    onSuccess: onPosted,
    onError: (failure: unknown) => {
      if (isUnknownOutcome(failure)) {
        setUnknown(true);
        return;
      }
      setError({
        text: intakeErrorMessage(failure, 'The goods could not be received into stock.'),
        conflict: failure instanceof ConflictError,
      });
    },
  });

  const check = useMutation({
    mutationFn: () => goodsIntakeApi.getReceipt(receipt.id),
    onSuccess: (current) => {
      if (current.state === 'POSTED') onPosted({ receipt: current, replayed: true });
      else setUnknown(false);
    },
  });

  const putAway = receipt.lines.filter((line) => acceptedQuantity(line) > 0);
  const differences = receipt.lines.flatMap((line) =>
    lineDiscrepancies(line).map((d) => ({ ...d, line })),
  );

  return (
    <Dialog title="Receive into stock" onClose={onClose}>
      {putAway.length > 0 ? (
        <table className="intake-summary">
          <caption>Into stock</caption>
          <thead>
            <tr>
              <th scope="col">Stock item</th>
              <th scope="col">Adds</th>
              <th scope="col">Location</th>
              <th scope="col">On hand after</th>
            </tr>
          </thead>
          <tbody>
            {putAway.map((line) => {
              const accepted = acceptedQuantity(line);
              const current =
                references.balances.find(
                  (b) => b.stockItemId === line.stockItemId && b.locationId === line.locationId,
                )?.onHand ?? 0;
              const unit = references.unit(line.stockItemId);
              return (
                <tr key={line.id}>
                  <td>{references.itemName(line.stockItemId)}</td>
                  <td>
                    {accepted} {unit}
                  </td>
                  <td>{references.locationName(line.locationId)}</td>
                  <td>
                    {current + accepted} {unit}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p>Nothing goes into stock: every line is short or damaged.</p>
      )}
      {differences.length > 0 ? (
        <div className="warning-callout">
          <strong>Recorded as discrepancies</strong>
          <ul>
            {differences.map((d) => (
              <li key={`${d.line.id}-${d.kind}`}>
                {discrepancyKindLabels[d.kind]}: {d.quantity} {references.unit(d.line.stockItemId)}{' '}
                {references.itemName(d.line.stockItemId)}
                {d.kind === 'DAMAGED' && d.line.note ? ` (${d.line.note})` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="field-hint">Everything matches the manifest.</p>
      )}
      <p className="field-hint">This can’t be undone. Corrections are made as stock adjustments.</p>

      {error ? (
        <div role="alert" className="form-error">
          {error.text}
          {error.conflict ? (
            <button type="button" className="link-button" onClick={onReload}>
              Reload receipt
            </button>
          ) : null}
        </div>
      ) : null}
      {unknown ? (
        <div role="alert" className="form-error partial-failure-inline">
          The server didn’t answer, so it’s not known whether the goods were received. Check before
          doing anything else; trying again never receives them twice.
          <button
            type="button"
            className="link-button"
            onClick={() => check.mutate()}
            disabled={check.isPending}
          >
            {check.isPending ? 'Checking…' : 'Check the receipt'}
          </button>
        </div>
      ) : null}
      <div className="drawer-actions">
        <button type="button" onClick={onClose}>
          Not yet
        </button>
        <button
          type="button"
          className="primary"
          onClick={() => {
            setError(null);
            post.mutate();
          }}
          disabled={post.isPending || error?.conflict === true}
        >
          {post.isPending ? 'Receiving…' : unknown ? 'Try again' : 'Receive into stock'}
        </button>
      </div>
    </Dialog>
  );
}

export function CancelReceiptDialog({
  receipt,
  onClose,
  onDone,
  onReload,
}: {
  receipt: ExpectedReceipt;
  onClose: () => void;
  onDone: (receipt: ExpectedReceipt) => void;
  onReload: () => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<{ text: string; conflict: boolean } | null>(null);
  const cancel = useMutation({
    mutationFn: () => goodsIntakeApi.cancelReceipt(receipt.id, receipt.version, reason),
    onSuccess: onDone,
    onError: (failure: unknown) =>
      setError({
        text: intakeErrorMessage(failure, 'The receipt could not be cancelled.'),
        conflict: failure instanceof ConflictError,
      }),
  });
  return (
    <Dialog title="Cancel receipt" onClose={onClose}>
      <p>Nothing is put into stock. Use this when the delivery won’t come.</p>
      <label>
        Reason (required)
        <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={2} />
      </label>
      {error ? (
        <div role="alert" className="form-error">
          {error.text}
          {error.conflict ? (
            <button type="button" className="link-button" onClick={onReload}>
              Reload receipt
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="drawer-actions">
        <button type="button" onClick={onClose}>
          Keep receipt
        </button>
        <button
          type="button"
          className="button-danger"
          onClick={() => cancel.mutate()}
          disabled={!reason.trim() || cancel.isPending}
        >
          {cancel.isPending ? 'Cancelling…' : 'Cancel receipt'}
        </button>
      </div>
    </Dialog>
  );
}
